import React from "react";
import { FirebaseUser } from "../types/user";
import { getMarketingRecipients } from "../services/adminService";
import "./stylesheets/EmailUsersModal.css";

interface EmailUsersModalProps {
  users: FirebaseUser[];
  onClose: () => void;
}

const EmailUsersModal: React.FC<EmailUsersModalProps> = ({
  users,
  onClose,
}) => {
  const [busy, setBusy] = React.useState(false),
    [error, setError] = React.useState("");
  const prepareEmail = async (selected: FirebaseUser[]) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const emails = await getMarketingRecipients(
        selected.map((user) => user.uid),
      );
      if (!emails.length) {
        setError(
          "No eligible recipients remain. Preferences may have changed.",
        );
        return;
      }
      window.location.href = `mailto:?bcc=${encodeURIComponent(emails.join(","))}&body=${encodeURIComponent("\n\nTo unsubscribe from MockMedic marketing, visit https://mockmedic.com/account or email support@mockmedic.com.")}`;
    } catch (e: any) {
      setError(e.message || "Unable to verify current marketing preferences.");
    } finally {
      setBusy(false);
    }
  };
  // Lock body scroll when modal is open
  React.useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleEmailAllUsers = () => {
    void prepareEmail(users);
  };

  const handleEmailSubbedUsers = () => {
    const subbedUsers = users.filter(
      (user) =>
        user.marketingConsent?.status === "subscribed" &&
        user.emailVerified &&
        !user.disabled &&
        Array.isArray(user.unlockedSpecialties) &&
        user.unlockedSpecialties.length > 0,
    );
    void prepareEmail(subbedUsers);
  };

  const handleEmailUnsubbedUsers = () => {
    const unsubbedUsers = users.filter(
      (user) =>
        user.marketingConsent?.status === "subscribed" &&
        user.emailVerified &&
        !user.disabled &&
        (!Array.isArray(user.unlockedSpecialties) ||
          user.unlockedSpecialties.length === 0),
    );
    void prepareEmail(unsubbedUsers);
  };

  const allUsersCount = users.filter(
    (user) =>
      user.marketingConsent?.status === "subscribed" &&
      user.emailVerified &&
      !user.disabled,
  ).length;
  const subbedUsersCount = users.filter(
    (user) =>
      user.marketingConsent?.status === "subscribed" &&
      user.emailVerified &&
      !user.disabled &&
      Array.isArray(user.unlockedSpecialties) &&
      user.unlockedSpecialties.length > 0,
  ).length;
  const unsubbedUsersCount = users.filter(
    (user) =>
      user.marketingConsent?.status === "subscribed" &&
      user.emailVerified &&
      !user.disabled &&
      (!Array.isArray(user.unlockedSpecialties) ||
        user.unlockedSpecialties.length === 0),
  ).length;

  return (
    <div className="email-modal-overlay" onClick={onClose}>
      <div className="email-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="email-modal-header">
          <h2>Email Users</h2>
          <button className="email-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="email-modal-body">
          {error && <p role="alert">{error}</p>}
          {busy && <p role="status">Checking current marketing preferences…</p>}
          <p>
            Only verified users with a recorded current marketing opt-in are
            included. Include an unsubscribe route in every marketing email.
          </p>

          <div className="email-options">
            <button
              className="email-option-button"
              onClick={handleEmailAllUsers}
              disabled={busy || allUsersCount === 0}
            >
              <div className="email-option-title">Email opted-in users</div>
              <div className="email-option-count">{allUsersCount} users</div>
            </button>

            <button
              className="email-option-button"
              onClick={handleEmailSubbedUsers}
              disabled={busy || subbedUsersCount === 0}
            >
              <div className="email-option-title">
                Opted-in users with specialties
              </div>
              <div className="email-option-count">
                {subbedUsersCount} users with specialties
              </div>
            </button>

            <button
              className="email-option-button"
              onClick={handleEmailUnsubbedUsers}
              disabled={busy || unsubbedUsersCount === 0}
            >
              <div className="email-option-title">
                Opted-in users without specialties
              </div>
              <div className="email-option-count">
                {unsubbedUsersCount} users without specialties
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailUsersModal;
